import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import './Gallery.css';

const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [newComments, setNewComments] = useState({});
  const [user] = useAuthState(auth);
  const [previewIndex, setPreviewIndex] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'gallery'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updated = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          likes: data.likes || 0,
          likedBy: data.likedBy || [],
          comments: data.comments || [],
        };
      });
      setPhotos(updated);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (previewIndex === null) return;
      if (e.key === 'ArrowLeft') {
        setPreviewIndex((previewIndex - 1 + photos.length) % photos.length);
      } else if (e.key === 'ArrowRight') {
        setPreviewIndex((previewIndex + 1) % photos.length);
      } else if (e.key === 'Escape') {
        setPreviewIndex(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewIndex, photos.length]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setPreviewIndex((previewIndex + 1) % photos.length),
    onSwipedRight: () => setPreviewIndex((previewIndex - 1 + photos.length) % photos.length),
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  const handleLike = async (photoId, photo) => {
    if (!user?.displayName) return alert('You must be logged in to like a photo.');

    const photoRef = doc(db, 'gallery', photoId);
    const hasLiked = photo.likedBy.includes(user.displayName);

    try {
      await updateDoc(photoRef, {
        likes: increment(hasLiked ? -1 : 1),
        likedBy: hasLiked
          ? arrayRemove(user.displayName)
          : arrayUnion(user.displayName),
      });
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleCommentChange = (photoId, value) => {
    setNewComments(prev => ({ ...prev, [photoId]: value }));
  };

  const handleAddComment = async (photoId) => {
    const text = newComments[photoId]?.trim();
    if (!text || !user?.displayName) return;

    const comment = {
      name: user.displayName,
      text,
      timestamp: new Date().toISOString(),
    };

    const photoRef = doc(db, 'gallery', photoId);
    try {
      await updateDoc(photoRef, {
        comments: arrayUnion(comment),
      });
      setNewComments(prev => ({ ...prev, [photoId]: '' }));
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDeleteComment = async (photoId, comment) => {
    const photoRef = doc(db, 'gallery', photoId);
    try {
      await updateDoc(photoRef, {
        comments: arrayRemove(comment),
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  return (
    <div className="gallery">
      {previewIndex !== null && (
        <div className="preview-overlay" onClick={() => setPreviewIndex(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()} {...swipeHandlers}>
            <img className="preview-image" src={photos[previewIndex].imageUrl} alt="Preview" />
            <button className="close-preview" onClick={() => setPreviewIndex(null)}>✕</button>
            <button
              onClick={() => navigate('/upload')}
              className="back-button"
            >
              ← Back to Upload More Photos
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => navigate('/upload')}
      >
        ← Upload More Photos
      </button>
      <h2>Team Gallery</h2>
      <div className="photo-grid">
        {photos.map((photo, index) => (
          <div key={photo.id} className="photo-card">
            <img src={photo.imageUrl} alt="Team" onClick={() => setPreviewIndex(index)} />

            {/* Like Button */}
            <div className="photo-actions">
              <button
                onClick={() => handleLike(photo.id, photo)}
                style={{ color: photo.likedBy.includes(user?.displayName) ? '#ff4f4f' : undefined }}
              >
                ❤️ {photo.likes}
              </button>
            </div>

            {/* Comment Section */}
            <input
              type="text"
              className="comment-input"
              placeholder="Leave a comment"
              value={newComments[photo.id] || ''}
              onChange={(e) => handleCommentChange(photo.id, e.target.value)}
            />
            <button
              className="comment-button"
              onClick={() => handleAddComment(photo.id)}
            >
              Comment
            </button>

            {/* Display Comments */}
            {photo.comments.length > 0 && (
              <div className="comment-list">
                {photo.comments.map((comment, i) => (
                  <div className="comment-entry" key={i}>
                    <span>
                      <strong>{comment.name}</strong>: {comment.text}
                    </span>
                    {comment.name === user?.displayName && (
                      <button onClick={() => handleDeleteComment(photo.id, comment)}>
                        🗑
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Gallery;
